<script setup lang="ts">
import get from 'lodash/get'
import { separateNumberFromDirection } from '~/lib/geoUtils'
import { isAutocompletePrediction, isGeocoderResult } from '~/lib/interfaces/helpers'
import { SelectedLocation } from '~/lib/interfaces/restaurant'
const { t } = useLocale()

const props = defineProps<{
  selectedLocation: SelectedLocation
  nextPage: () => void
}>()

const { resetPlaces, searchPlacesWithoutDebounce } = useGooglePlaces()

const direction = computed(() => {
  if (isAutocompletePrediction(props.selectedLocation, 'description')) {
    const {
      structured_formatting: { main_text: mainText },
      description,
    } = props.selectedLocation
    return separateNumberFromDirection({ mainText: mainText || description })
  } else {
    return {
      number: getValueFromAddressComponents({
        key: 'street_number',
        place: props.selectedLocation,
      }),
      direction:
        getValueFromAddressComponents({
          key: 'route',
          place: props.selectedLocation,
        }) ||
        getValueFromAddressComponents({
          key: 'street_address',
          place: props.selectedLocation,
        }),
    }
  }
})

const additionalInfoDeliveryError = computed(() => {
  return (
    (!number.value && hasNumber.value && !additionalInfoDelivery.value) ||
    (!additionalInfoDelivery.value && hasNumber.value)
  )
})

const number = ref(direction.value.number)
const textDirection = ref(direction.value.direction)
const hasNumber = ref(false)

const additionalInfo = ref<string>('')
const additionalInfoDelivery = ref<string>('')
const favoriteChecked = ref<boolean>(false)
const favoriteAlias = ref<string>('')

const restaurantStore = useRestaurantStore()

const disabledContinue = computed(() => {
  if (!number.value && !hasNumber.value) return true
  else if (additionalInfoDeliveryError.value) return true
  else if (favoriteChecked.value && !favoriteAlias.value) return true
  return false
})

const onContinue = async () => {
  restaurantStore.updateAddress({
    number: hasNumber.value ? '' : number.value,
    additionalInfo: additionalInfo.value,
    additionalInfoDelivery: additionalInfoDelivery.value,
    favoriteAlias: favoriteChecked.value ? favoriteAlias.value : undefined,
  })

  if (number.value) {
    const isGeocoderLocation = isGeocoderResult(props.selectedLocation, 'formatted_address')
    const secondaryText: string = get(
      props.selectedLocation,
      'structured_formatting.secondary_text',
      ''
    )
    const searchQuery = isGeocoderLocation
      ? extractGeocoderPlaceStr(props.selectedLocation, number.value)
      : `${restaurantStore?.selectedRestaurant?.deliveryAddress?.address} ${number.value} ${
          secondaryText ? ' ' + secondaryText : ''
        }`
    const places = await searchPlacesWithoutDebounce(searchQuery)

    if (places && places.length > 0) {
      const location = places[0]
      useGoogleSessionToken()
      const address = await getAddressInfo(location.place_id)
      if (!address) return
      restaurantStore.setGoogleLocation({
        ...location,
        lat: address.latitude,
        lng: address.longitude,
      })
      resetPlaces()
    }
  }

  props.nextPage()
}

watch(hasNumber, () => {
  number.value = ''
})
</script>

<template>
  <ModalCenter :show-close-icon="false" :add-close-button="false" :content-full="true">
    <template #header>
      <div class="flex items-center gap-2">
        <img
          class="flex-start h-8 cursor-pointer md:h-6"
          src="/images/icons/arrowLeft.svg"
          alt="arrowLeft"
          @click="restaurantStore.closeModal()"
        />
        <p class="md:hidden">{{ t('selectRestaurant.DLV.completeDirection') }}</p>
      </div>
    </template>
    <template #content>
      <div class="mx-4 mt-8 flex h-full flex-col text-left md:mx-14">
        <div class="hidden md:block">
          <h2 class="text-2xl font-bold">{{ t('selectRestaurant.DLV.completeDirection') }}</h2>
        </div>
        <div class="flex flex-wrap justify-between gap-10 md:mt-8 md:gap-4">
          <div class="flex max-w-[328px] flex-col">
            <p class="mb-3 text-[10px] text-mcd-secondaryDarkGrey">
              {{ t('selectRestaurant.DLV.street') }}
            </p>
            <p class="text-sm">{{ textDirection }}</p>
            <p class="text-[10px]">
              {{ restaurantStore?.selectedRestaurant?.deliveryAddress?.city }},
              {{ restaurantStore?.selectedRestaurant?.deliveryAddress?.state }}
            </p>
          </div>
          <div class="flex justify-between">
            <div>
              <p class="mb-3 text-[10px] text-mcd-secondaryDarkGrey">
                {{ t('selectRestaurant.DLV.number') }}
              </p>
              <div class="flex gap-4">
                <input
                  v-model="number"
                  type="text"
                  class="w-52 border-b-[1px] border-mcd-black placeholder-secondaryDarkGrey placeholder:text-sm focus:placeholder-opacity-0"
                  :placeholder="t('selectRestaurant.DLV.number')"
                  :disabled="hasNumber"
                />
                <div class="flex items-center gap-2">
                  <input id="hasNumber" v-model="hasNumber" type="checkbox" />
                  <label for="hasNumber" class="text-xs text-mcd-secondaryDarkGrey">
                    {{ t('selectRestaurant.DLV.noNumber') }}
                  </label>
                </div>
              </div>
              <div class="mt-2 h-4">
                <div v-show="!number && !hasNumber" class="flex gap-2 text-mcd-red">
                  <img src="/images/icons/errorDLVmap.svg" alt="error icon" />
                  <p class="text-xs">{{ t('selectRestaurant.DLV.addAddressError') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-10 w-full">
          <input
            v-model="additionalInfo"
            class="w-full border-b-[1px] border-mcd-secondaryLightGrey pb-2 placeholder-secondaryDarkGrey placeholder:text-sm focus:placeholder-opacity-0"
            type="text"
            :placeholder="t('selectRestaurant.DLV.additionalInfoPlaceholder')"
          />
        </div>
        <div
          class="mt-8 w-full"
          :class="additionalInfoDeliveryError ? 'text-mcd-red' : 'text-mcd-secondaryDarkGrey'"
        >
          <label for="additionalInfo" class="mb-2 flex justify-between text-[10px]">
            {{ t('selectRestaurant.DLV.additionalInfo') }}
            <p
              :class="{
                'text-mcd-red': additionalInfoDelivery.length > 280,
              }"
            >
              {{ additionalInfoDelivery.length }}/280
            </p>
          </label>
          <textarea
            v-model="additionalInfoDelivery"
            class="w-full border-b-[1px] py-2 placeholder-secondaryDarkGrey placeholder:text-sm focus:placeholder-opacity-0"
            :class="
              additionalInfoDeliveryError ? 'border-mcd-red' : 'border-mcd-secondaryLightGrey '
            "
            type="text"
            rows="1"
            :placeholder="t('selectRestaurant.DLV.additionalInfoDeliveryPlaceholder')"
            maxlength="280"
          ></textarea>

          <div class="h-4">
            <div v-if="additionalInfoDeliveryError" class="flex gap-2 text-mcd-red">
              <img src="/images/icons/errorDLVmap.svg" alt="error icon" />
              <p class="text-xs">{{ t('selectRestaurant.DLV.addAddressError') }}</p>
            </div>
          </div>
        </div>

        <!-- Save favorite address -->
        <DLVFavoriteAddressSave
          class="mt-auto"
          :checked="favoriteChecked"
          :alias="favoriteAlias"
          @update:checked="favoriteChecked = $event"
          @update:alias="favoriteAlias = $event"
        />
      </div>
    </template>

    <template #actions>
      <div class="mr-14 w-full md:mb-14">
        <McdButton
          :disabled="disabledContinue"
          is-primary
          class="btn-primary ml-auto mr-0 h-16 w-full md:mr-14 md:h-fit md:w-fit"
          @click="onContinue()"
        >
          {{ t('common.continue') }}
        </McdButton>
      </div>
    </template>
  </ModalCenter>
</template>

<style>
input[type='checkbox'] {
  width: 16px;
  height: 16px;
}
</style>
